"use client"

import * as React from "react"
import { motion, type TargetAndTransition, type Variant } from "motion/react"

import { cn } from "@/core/utils/utils"

export interface MotionBlurRevealProps {
  text: string
  className?: string
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div"
  mode?: "words" | "chars" | "lines"
  direction?: "up" | "down" | "left" | "right"
  blurAmount?: number
  scaleAmount?: number
  delay?: number
  duration?: number
  stagger?: number
  trigger?: "viewport" | "load"
  viewportMargin?: string
  once?: boolean
  onComplete?: () => void
}

/**
 * Awwwards SOTD-grade Motion Blur Text Reveal Component.
 *
 * Implements the "Kinetic Snap" technique:
 * 1. High initial velocity cubic-bezier deceleration [0.16, 1, 0.3, 1]
 * 2. Blur decay (blur(12px) -> blur(0px)) coupled with directional translation
 * 3. Automatic filter eviction on completion (clears GPU raster cache & restores subpixel font anti-aliasing)
 * 4. Full screen-reader accessibility via aria-label + aria-hidden
 */
export function MotionBlurReveal({
  text,
  className,
  tag = "div",
  mode = "words",
  direction = "up",
  blurAmount = 12,
  scaleAmount = 0.96,
  delay = 0,
  duration = 0.65,
  stagger = 0.03,
  trigger = "viewport",
  viewportMargin = "-50px",
  once = true,
  onComplete,
}: MotionBlurRevealProps) {
  const [isCompleted, setIsCompleted] = React.useState(false)

  // Calculate directional offset
  const getInitialTransform = (): { x: number; y: number } => {
    switch (direction) {
      case "up":
        return { x: 0, y: 32 }
      case "down":
        return { x: 0, y: -32 }
      case "left":
        return { x: 32, y: 0 }
      case "right":
        return { x: -32, y: 0 }
      default:
        return { x: 0, y: 32 }
    }
  }

  const { x, y } = getInitialTransform()

  // Tokenize text
  const tokens = React.useMemo(() => {
    if (mode === "chars") {
      return text.split("").map((char, index) => ({
        id: `${index}-${char}`,
        value: char === " " ? "\u00A0" : char,
        isSpace: char === " ",
      }))
    }

    if (mode === "lines") {
      return text.split("\n").map((line, index) => ({
        id: `${index}-${line}`,
        value: line,
        isSpace: false,
      }))
    }

    // Default: 'words'
    return text.split(" ").map((word, index) => ({
      id: `${index}-${word}`,
      value: word,
      isSpace: false,
    }))
  }, [text, mode])

  // Custom Awwwards SOTD easing curve: extreme initial punch, buttery settle
  const sotdEase = [0.16, 1, 0.3, 1] as const

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger,
        delayChildren: delay,
      },
    },
  }

  const tokenVariants: { hidden: Variant; visible: TargetAndTransition } = {
    hidden: {
      opacity: 0,
      x,
      y,
      scale: scaleAmount,
      filter: `blur(${blurAmount}px)`,
      willChange: "transform, opacity, filter",
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        duration,
        ease: sotdEase,
      },
    },
  }

  const handleAnimationComplete = React.useCallback(() => {
    setIsCompleted(true)
    onComplete?.()
  }, [onComplete])

  const Tag = motion[tag] as typeof motion.div

  const animationProps =
    trigger === "viewport"
      ? {
          initial: "hidden",
          whileInView: "visible",
          viewport: { once, margin: viewportMargin as any },
        }
      : {
          initial: "hidden",
          animate: "visible",
        }

  return (
    <Tag
      className={cn(
        "inline-flex flex-wrap gap-x-[0.28em] gap-y-[0.1em]",
        className
      )}
      variants={containerVariants}
      {...animationProps}
      aria-label={text}
      onAnimationComplete={handleAnimationComplete}
    >
      {tokens.map((token, index) => (
        <span
          key={token.id}
          className="inline-block overflow-visible"
          aria-hidden="true"
        >
          <motion.span
            variants={tokenVariants}
            className="inline-block origin-bottom"
            style={{
              // Once animation completes, completely evict filter property to restore 100% native subpixel font rendering
              filter: isCompleted ? "none" : undefined,
              willChange: isCompleted ? "auto" : "transform, opacity, filter",
            }}
          >
            {token.value}
          </motion.span>
        </span>
      ))}
    </Tag>
  )
}
